"use client";

import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { detailsNav } from "@/data/packageDetailsNav";
import Overview from "./Overview";
import Inclusions from "./Inclusions";
import { Itenary } from "./Itenary";
import CancellationPolicy from "./CancellationPolicy";
import { IPackageDetailsData } from "../interface/IPackageDetails.interface";
import Accomodation from "./Accomodation";
import PackageSummary from "./PackageSummary";
import PromoCode from "./PromoCode";
import Rating from "./Rating";

interface IProps {
  packageData: IPackageDetailsData;
  orgData?: any;
}

const PackageOverview: React.FC<IProps> = ({ packageData, orgData }) => {
  const [activeSection, setActiveSection] = useState<string>("overview");
  const observer = useRef<IntersectionObserver | null>(null);

  const scrollToSection = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      const element = document.getElementById(id);
      if (element) {
        const offset = 180;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elementPosition - offset,
          behavior: "smooth",
        });
      }
    },
    []
  );

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0px -70% 0px",
        threshold: 0,
      }
    );

    detailsNav.forEach((item) => {
      const sectionId = item.href.replace("#", "");
      const element = document.getElementById(sectionId);
      if (element) observer.current?.observe(element);
    });

    return () => observer.current?.disconnect();
  }, []);

  const sections = [
    {
      id: "overview",
      component: <Overview overview={packageData?.itinerary} />,
    },
    {
      id: "itinerary",
      component: (
        <Itenary
          itinerary={packageData?.itinerary}
          activity={packageData?.activity}
          meals={packageData?.meals}
        />
      ),
    },
    {
      id: "accommodation",
      component: <Accomodation accomodation={packageData?.accommodations} />,
    },
    {
      id: "inclusions",
      component: (
        <Inclusions
          inclusions={packageData?.inclusions}
          exclusions={packageData?.exclusions}
          notices={packageData?.notices}
        />
      ),
    },
    {
      id: "cancellationPolicy",
      component: (
        <CancellationPolicy
          paymentPolicy={packageData?.payment_policy}
          cancellationPolicy={packageData?.cancellation_policy}
          termsAndConditions={packageData?.terms_conditions}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen pb-10">
      <div className="flex flex-col lg:flex-row gap-10">
        <div className="flex-1 flex flex-col gap-10">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="sticky top-0 z-40 py-4 bg-slate-50/50"
          >
            <nav className="bg-white/80 backdrop-blur-xl border border-slate-200 p-4 flex items-center gap-1 overflow-x-auto no-scrollbar rounded-2xl shadow-sm">
              {detailsNav.map((item) => {
                const sectionId = item.href.replace("#", "");
                const isActive = activeSection === sectionId;
                return (
                  <button
                    key={item.href}
                    onClick={(e: any) => scrollToSection(e, sectionId)}
                    className="relative px-6 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-colors duration-300"
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-primary-500 rounded-full"
                        transition={{
                          type: "spring",
                          bounce: 0.2,
                          duration: 0.6,
                        }}
                      />
                    )}
                    <span
                      className={`relative z-10 ${isActive ? "text-white" : "text-slate-500 hover:text-primary-600"}`}
                    >
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </motion.div>

          <div className="flex flex-col gap-10">
            {sections.map((section) => (
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                key={section.id}
                id={section.id}
                className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm scroll-mt-48"
              >
                {section.component}
              </motion.section>
            ))}
          </div>
        </div>

        <aside className="w-full lg:w-80 shrink-0">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:sticky lg:top-24 space-y-6"
          >
            <PackageSummary
              groupSize={packageData?.group_size}
              currentPrice={packageData?.current_price}
              previousPrice={packageData?.previous_price}
            />
            <PromoCode orgData={orgData?.data} />
            <Rating packageId={packageData?.id} />
          </motion.div>
        </aside>
      </div>
    </div>
  );
};

export default PackageOverview;
